import { z } from "zod";
export const ConversationAttachmentSchema = z
    .object({
    objectKey: z.string(),
    name: z.string(),
    mimeType: z.string(),
    size: z.number().nonnegative(),
    status: z.enum(["ready", "failed"]),
    expiredAt: z.string().optional(),
    workspacePath: z.string().optional(),
})
    .strict();
const ToolSchema = z
    .object({
    toolCallId: z.string(),
    toolName: z.string(),
    paramsSummary: z.string(),
    status: z.enum(["running", "completed", "failed", "incomplete", "awaiting_approval"]),
    argsText: z.string().optional(),
    resultText: z.string().optional(),
    timing: z
        .object({ startedAt: z.number(), completedAt: z.number().optional() })
        .strict()
        .optional(),
})
    .strict();
const DecisionSchema = z
    .object({
    confirmationId: z.string(),
    toolCallId: z.string().optional(),
    toolName: z.string(),
    summary: z.string(),
    state: z.enum(["approved", "denied", "expired", "cancelled"]),
    resolvedBy: z.string().optional(),
    resolvedByName: z.string().optional(),
    resolvedAt: z.string(),
})
    .strict();
export const ConversationSubmissionSchema = z
    .object({
    deliveryId: z.string(),
    clientMessageId: z.string(),
    principalId: z.string(),
    acceptedAt: z.string(),
})
    .strict();
export const ConversationItemSchema = z
    .object({
    id: z.string(),
    kind: z.enum(["user_message", "assistant_message", "notice", "compaction"]),
    role: z.enum(["user", "assistant"]),
    runId: z.string().optional(),
    state: z.enum(["accepted", "streaming", "committed", "interrupted"]),
    availability: z.enum(["complete", "limited", "unavailable"]),
    timestamp: z.string(),
    text: z.string(),
    blocks: z
        .array(z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("text"), text: z.string() }).strict(),
        z.object({ kind: z.literal("tool"), toolCallId: z.string() }).strict(),
    ]))
        .optional(),
    participant: z.object({ platform: z.string(), userId: z.string() }).strict().optional(),
    submission: ConversationSubmissionSchema.optional(),
    toolEvents: z.array(ToolSchema).optional(),
    approvalEvents: z.array(DecisionSchema).optional(),
    attachments: z.array(ConversationAttachmentSchema).optional(),
    compaction: z
        .object({ trigger: z.enum(["requested", "automatic"]), keptRows: z.number() })
        .strict()
        .optional(),
    gatewayNotice: z
        .discriminatedUnion("kind", [
        z.object({ kind: z.literal("turn_failure"), runId: z.string() }).strict(),
        z.object({ kind: z.literal("schedule_pause"), taskId: z.string() }).strict(),
        z
            .object({
            kind: z.literal("confirmation_unapplied"),
            runId: z.string(),
            confirmationIds: z.array(z.string()),
        })
            .strict(),
    ])
        .optional(),
    turnTiming: z.object({ startedAt: z.number(), completedAt: z.number() }).strict().optional(),
    outcome: z.enum(["stopped", "error"]).optional(),
    errorKind: z
        .enum(["refusal", "timeout", "rate_limit", "context_length", "egress_refresh", "unknown"])
        .optional(),
})
    .strict();
export const ConversationRunSchema = z
    .object({
    runId: z.string(),
    status: z.enum(["working", "input-required", "completed", "failed", "cancelled", "unknown"]),
    phase: z.enum(["queued", "admitted", "starting_model"]).optional(),
    deliveryIds: z.array(z.string()),
    startedAt: z.string().optional(),
    completedAt: z.string().optional(),
    durable: z.boolean(),
})
    .strict();
export const ConversationApprovalSchema = z
    .object({
    confirmationId: z.string(),
    runId: z.string(),
    messageId: z.string().optional(),
    toolCallId: z.string(),
    tool: z.string(),
    summary: z.string(),
    preview: z.string().optional(),
    requestedAt: z.string(),
    expiresAt: z.string(),
})
    .strict();
export const ConversationWindowSchema = z
    .object({
    itemIds: z.array(z.string()),
    hasMore: z.boolean(),
    before: z.string().nullable(),
})
    .strict();
const Position = { epoch: z.string(), revision: z.number().int().nonnegative() };
const View = {
    window: ConversationWindowSchema,
    items: z.array(ConversationItemSchema),
    runs: z.array(ConversationRunSchema),
    approvals: z.array(ConversationApprovalSchema),
};
export const ConversationFrameSchema = z.discriminatedUnion("type", [
    z
        .object({
        type: z.literal("conversation_snapshot"),
        ...Position,
        sessionId: z.string(),
        ...View,
    })
        .strict(),
    z
        .object({
        type: z.literal("conversation_changed"),
        ...Position,
        ...View,
        removedItemIds: z.array(z.string()),
    })
        .strict(),
    z
        .object({
        type: z.literal("conversation_unavailable"),
        ...Position,
        state: z.enum(["stale", "unavailable"]),
        retryable: z.boolean(),
    })
        .strict(),
]);
export const ConversationPageSchema = z.object({ ...Position, ...View }).strict();
export const ConversationItemReadSchema = z
    .object({ ...Position, item: ConversationItemSchema })
    .strict();
export const ConversationAcceptedSchema = z
    .object({
    ok: z.literal(true),
    outcome: z.literal("accepted"),
    sessionId: z.string(),
    sessionKey: z.string(),
    clientMessageId: z.string(),
    deliveryId: z.string(),
    acceptedAt: z.string(),
    replayed: z.boolean(),
})
    .strict();
export { ConversationView } from "./conversation-view.js";
